import { useState } from "react"
import {ChevronDown, HelpCircle} from 'lucide-react'

const faqs = [
  {
    question: "How do I calculate a land contract payoff?",
    answer:
      "To calculate a land contract payoff, you need to know your remaining balance, interest rate, and any balloon payment terms. Enter your original land price, down payment, interest rate, and loan term into our calculator above. If your land contract includes a balloon payment, enable the balloon payment toggle and set the due date. The calculator will show your remaining balance at any point during the contract, which represents your payoff amount. Remember to also account for any prepayment penalties or fees specified in your land contract agreement.",
  },
  {
    question: "What is a typical down payment on land?",
    answer:
      "A typical down payment on land ranges from 20% to 50% of the purchase price, depending on the type of land and financing method. For improved lots (with utilities and road access), lenders may accept 20-25% down. Raw land or undeveloped parcels typically require 30-50% down due to higher risk. Land contracts (owner financing) may offer more flexibility, sometimes accepting 10-20% down. Our land payment estimator lets you experiment with different down payment amounts to see how they affect your monthly payments and total cost. Use our calculator to compare scenarios with both dollar amounts and percentage-based down payments.",
  },
  {
    question: "How does a land interest rate calculator work?",
    answer:
      "A land interest rate calculator uses the standard amortization formula to compute your monthly payments based on the loan amount, annual interest rate, and loan term. Land loan interest rates are typically 1-3% higher than residential mortgage rates, ranging from 6% to 12% depending on the lender, land type, and your credit profile. Our calculator converts the annual rate to a monthly rate, then calculates the fixed monthly payment that will fully amortize the loan over your chosen term. The amortization schedule shows exactly how each payment is split between principal reduction and interest charges, giving you complete transparency into your land financing costs.",
  },
  {
    question: "What are common land closing costs?",
    answer:
      "Common land closing costs typically range from 2% to 5% of the purchase price and include: title search and insurance ($500-$2,000), survey costs ($300-$1,500), appraisal fees ($300-$600), recording fees ($50-$250), attorney fees ($500-$1,500), and origination fees (0.5-1% of loan amount). Environmental assessments may be required for certain properties. Land contracts (owner financing) often have lower closing costs since they bypass traditional lending requirements. When using our raw land mortgage calculator, factor these additional costs into your total budget to get an accurate picture of your complete land purchase investment.",
  },
  {
    question: "What is the difference between a land mortgage and a land contract?",
    answer:
      "A land mortgage involves a traditional lender (bank or credit union) providing a loan secured by the property, with the buyer receiving the deed at closing. A land contract (also called contract for deed) is seller financing where the seller retains the title until the buyer completes all payments. Land contracts often feature balloon payments, higher interest rates, and shorter terms (5-10 years) compared to traditional mortgages (15-30 years). Our calculator handles both scenarios — use the balloon payment feature for land contracts and standard terms for traditional land mortgages. The land note calculator function helps evaluate both financing options.",
  },
  {
    question: "How can extra payments reduce my land loan cost?",
    answer:
      "Extra payments on your land loan go directly toward principal reduction, which decreases the total interest you pay and shortens your loan term. For example, on a $120,000 land loan at 7.5% over 15 years, adding just $100 per month in extra payments can save over $15,000 in interest and pay off your loan nearly 3 years early. Use our land financing calculator's extra payment feature to see the exact impact on your specific loan. Even small additional amounts make a significant difference over time due to the compounding effect of reduced principal balances.",
  },
]

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0)

  return (
    <section className="py-20 bg-gray-50" id="faq">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-14">
          <span className="inline-block px-4 py-1.5 bg-emerald-50 text-emerald-800 rounded-full text-sm font-semibold mb-4 border border-emerald-200">
            <HelpCircle className="w-4 h-4 inline-block mr-1 -mt-0.5" />
            FAQ
          </span>
          <h2
            className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4"
            style={{ fontFamily: "'DM Serif Display', serif" }}
          >
            Frequently Asked Questions
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg">
            Common questions about land loans, land contracts, and how to use our land financing calculator effectively.
          </p>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index
            return (
              <div
                key={index}
                className={`bg-white rounded-xl border-2 transition-all duration-300 overflow-hidden ${
                  isOpen ? "border-emerald-200 shadow-md" : "border-gray-100 hover:border-gray-200"
                }`}
              >
                <button
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  className="w-full px-6 py-5 text-left flex items-center justify-between gap-4"
                  aria-expanded={isOpen}
                >
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 pr-4" style={{ fontFamily: "'DM Serif Display', serif" }}>
                    {faq.question}
                  </h3>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-300 ${
                      isOpen ? "rotate-180 text-emerald-600" : ""
                    }`}
                  />
                </button>
                {isOpen ? (
                  <div className="px-6 pb-5 animate-fade-in">
                    <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{faq.answer}</p>
                  </div>
                ) : null}
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
